import { useEffect, useRef, useState, useMemo } from 'react';
import { MapPin, Camera, Maximize2, X } from 'lucide-react';
import SimpleCameraStream from './SimpleCameraStream';

// Global cache for geocoding results to prevent repeated API calls
const geocodingCache = new Map();

const GoogleMapsView = ({ cameras, onCameraClick }) => {
    console.log('🗺️ GoogleMapsView component rendered with:', {
        cameraCount: cameras?.length || 0,
        cameraNames: cameras?.map(c => c.name) || []
    });
    
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);
    const cameraMarkersRef = useRef(new Map()); // Track which cameras have markers
    const [mapLoaded, setMapLoaded] = useState(false);
    const [apiBlocked, setApiBlocked] = useState(false);
    const [camerasWithCoords, setCamerasWithCoords] = useState([]);
    const [geocodingInProgress, setGeocodingInProgress] = useState(false);
    const [mapInitialized, setMapInitialized] = useState(false);
    const [lastCameraCount, setLastCameraCount] = useState(0); // Track camera count to avoid unnecessary updates
    const [forceUpdate, setForceUpdate] = useState(0); // Force update counter for retry mechanism
    const [markersFullyLoaded, setMarkersFullyLoaded] = useState(false);
    const [currentZoom, setCurrentZoom] = useState(5);
    const [lastSignificantZoom, setLastSignificantZoom] = useState(5);
    const [selectedCamera, setSelectedCamera] = useState(null);

    // Get API key from environment variable
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    // Helper function to get camera coordinates, prioritizing coordinates field
    const getCameraCoordinates = (camera) => {
        // Priority 1: Check camera.coordinates.latitude and camera.coordinates.longitude first (correct database structure)
        if (camera.coordinates?.latitude && camera.coordinates?.longitude) {
            const lat = parseFloat(camera.coordinates.latitude);
            const lng = parseFloat(camera.coordinates.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                return {
                    lat,
                    lng,
                    source: 'coordinates'
                };
            }
        }
        
        // Priority 2: Fall back to camera.location fields for backward compatibility (old structure)
        if (camera.location?.latitude && camera.location?.longitude) {
            const lat = parseFloat(camera.location.latitude);
            const lng = parseFloat(camera.location.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                return {
                    lat,
                    lng,
                    source: 'location'
                };
            }
        }
        
        return null;
    };

    // Get cameras that already have coordinates (don't wait for geocoding)
    const camerasWithExistingCoords = cameras.filter(camera => {
        const coords = getCameraCoordinates(camera);
        return coords !== null;
    });

    console.log('📍 Cameras with coordinates (processed):', {
        total: camerasWithCoords.length,
        cameras: camerasWithCoords.map(c => ({
            name: c.name,
            lat: c.coordinates?.latitude,
            lng: c.coordinates?.longitude
        }))
    });

    // Use a simple debouncing approach with timeout
    const updateTimeoutRef = useRef(null);

    // Reset markers loaded state when cameras change
    useEffect(() => {
        setMarkersFullyLoaded(false);
    }, [cameras]);

    // Helper function to create a single camera marker
    const createSingleCameraMarker = (camera, lat, lng) => {
        try {
            const marker = new window.google.maps.Marker({
                position: { lat, lng },
                map: mapInstanceRef.current,
                title: camera.name,
                icon: {
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="20" cy="20" r="19" fill="white" stroke="#000" stroke-width="2"/>
                            <circle cx="20" cy="20" r="16" fill="${camera.isOnline ? '#10B981' : '#EF4444'}"/>
                            <circle cx="20" cy="20" r="12" fill="white"/>
                            <circle cx="20" cy="20" r="8" fill="${camera.isOnline ? '#10B981' : '#EF4444'}"/>
                            <rect x="14" y="14" width="12" height="8" rx="1" fill="white"/>
                            <rect x="16" y="16" width="8" height="4" rx="1" fill="${camera.isOnline ? '#10B981' : '#EF4444'}"/>
                            <circle cx="20" cy="18" r="1.5" fill="white"/>
                        </svg>
                    `)}`,
                    scaledSize: new window.google.maps.Size(40, 40),
                    anchor: new window.google.maps.Point(20, 20)
                }
            });

            marker.addListener('click', () => {
                console.log('Camera marker clicked:', camera.name);
                setSelectedCamera(camera);
            });

            return marker;
        } catch (error) {
            console.error(`Failed to create marker for camera ${camera.name}:`, error);
            return null;
        }
    };

    // Helper function to create a cluster marker
    const createClusterMarker = (cameras, lat, lng) => {
        try {
            const onlineCount = cameras.filter(c => c.isOnline).length;
            const offlineCount = cameras.length - onlineCount;
            const hasOnline = onlineCount > 0;
            const hasOffline = offlineCount > 0;
            
            // Determine cluster color based on camera states
            let clusterColor = '#6B7280'; // Default gray
            if (hasOnline && !hasOffline) {
                clusterColor = '#10B981'; // All online - green
            } else if (!hasOnline && hasOffline) {
                clusterColor = '#EF4444'; // All offline - red
            } else if (hasOnline && hasOffline) {
                clusterColor = '#F59E0B'; // Mixed - yellow
            }

            const marker = new window.google.maps.Marker({
                position: { lat, lng },
                map: mapInstanceRef.current,
                title: `${cameras.length} cameras (${onlineCount} online, ${offlineCount} offline)`,
                icon: {
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="25" cy="25" r="24" fill="white" stroke="#000" stroke-width="2"/>
                            <circle cx="25" cy="25" r="20" fill="${clusterColor}"/>
                            <circle cx="25" cy="25" r="16" fill="white"/>
                            <text x="25" y="30" text-anchor="middle" font-family="Arial" font-size="12" font-weight="bold" fill="${clusterColor}">${cameras.length}</text>
                        </svg>
                    `)}`,
                    scaledSize: new window.google.maps.Size(50, 50),
                    anchor: new window.google.maps.Point(25, 25)
                }
            });

            // Add click listener to show camera list
            marker.addListener('click', () => {
                // Create info window content
                const content = `
                    <div style="max-width: 300px;">
                        <h3 style="margin: 0 0 10px 0; color: #1F2937;">${cameras.length} Cameras at this location</h3>
                        <div style="max-height: 200px; overflow-y: auto;">
                            ${cameras.map(camera => `
                                <div style="display: flex; align-items: center; padding: 4px 0; border-bottom: 1px solid #E5E7EB;">
                                    <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${camera.isOnline ? '#10B981' : '#EF4444'}; margin-right: 8px;"></div>
                                    <span style="font-size: 14px; color: #374151;">${camera.name}</span>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                `;
                
                const infoWindow = new window.google.maps.InfoWindow({
                    content: content
                });
                
                infoWindow.open(mapInstanceRef.current, marker);
            });

            return marker;
        } catch (error) {
            console.error(`Failed to create cluster marker:`, error);
            return null;
        }
    };

    useEffect(() => {
        // Load Google Maps API
        const loadGoogleMaps = () => {
            if (window.google && window.google.maps) {
                console.log('Google Maps API already loaded, using cached instance');
                setMapLoaded(true);
                return;
            }

            // Check if script is already being loaded
            if (document.querySelector('script[src*="maps.googleapis.com"]')) {
                console.log('Google Maps API script already loading, waiting...');
                // Wait for the script to load
                const checkInterval = setInterval(() => {
                    if (window.google && window.google.maps) {
                        console.log('Google Maps API loaded from existing script');
                        setMapLoaded(true);
                        clearInterval(checkInterval);
                    }
                }, 100);
                return;
            }

            if (!apiKey) {
                console.warn('Google Maps API key not configured');
                setApiBlocked(true);
                setMapLoaded(true); // Set to true to show the error state
                return;
            }

            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                console.log('Google Maps API loaded successfully');
                setMapLoaded(true);
            };
            script.onerror = () => {
                console.error('Failed to load Google Maps API');
                setApiBlocked(true);
                setMapLoaded(true); // Set to true to show the error state
            };
            document.head.appendChild(script);
        };

        loadGoogleMaps();
    }, [apiKey]);

    // Initialize map immediately when API is loaded
    useEffect(() => {
        if (!mapLoaded || mapInitialized) return;

        // Check if Google Maps API is available
        if (!window.google || !window.google.maps) {
            console.error('Google Maps API not loaded');
            return;
        }

        // Check if map container exists
        if (!mapRef.current) {
            console.error('Map container not found');
            return;
        }

        // Check if map instance already exists (cached)
        if (mapInstanceRef.current) {
            console.log('Map instance already exists, reusing cached instance');
            setMapInitialized(true);
            // Force camera update when reusing existing map
            if (cameras && cameras.length > 0) {
                setTimeout(() => setForceUpdate(prev => prev + 1), 100);
            }
            return;
        }

        // Initialize map with default location
        const initMap = () => {
            if (mapInstanceRef.current) return;

            console.log('Initializing Google Maps...');

            // Default center (Center of India)
            const defaultCenter = { lat: 20.5937, lng: 78.9629 };

            try {
                mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                    center: defaultCenter,
                    zoom: 5,
                    mapTypeId: window.google.maps.MapTypeId.SATELLITE, // Set satellite as default
                    mapTypeControl: true, // Enable map type controls
                    mapTypeControlOptions: {
                        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                        position: window.google.maps.ControlPosition.TOP_RIGHT,
                        mapTypeIds: [
                            window.google.maps.MapTypeId.ROADMAP,
                            window.google.maps.MapTypeId.SATELLITE,
                            window.google.maps.MapTypeId.HYBRID,
                            window.google.maps.MapTypeId.TERRAIN
                        ]
                    },
                    streetViewControl: false,
                    fullscreenControl: true, // Enable fullscreen for better satellite viewing
                    fullscreenControlOptions: {
                        position: window.google.maps.ControlPosition.TOP_LEFT
                    },
                    zoomControl: true,
                    zoomControlOptions: {
                        position: window.google.maps.ControlPosition.RIGHT_CENTER
                    },
                    scaleControl: true // Add scale control for satellite view
                });

                console.log('Map initialized successfully with satellite view as default');
                
                // Add zoom change listener
                mapInstanceRef.current.addListener('zoom_changed', () => {
                    const newZoom = mapInstanceRef.current.getZoom();
                    console.log('Zoom changed to:', newZoom);
                    setCurrentZoom(newZoom);
                });
                
                // Set initial zoom
                setCurrentZoom(mapInstanceRef.current.getZoom());
                setMapInitialized(true);
                
                // Force an immediate update to load cameras if they're already available
                if (cameras && cameras.length > 0) {
                    console.log('Cameras available during map init, forcing immediate update...');
                    setTimeout(() => setForceUpdate(prev => prev + 1), 100);
                }
            } catch (error) {
                console.error('Failed to initialize Google Maps:', error);
                return;
            }
        };

        // Execute immediately when API is loaded
        initMap();

        // Cleanup function
        return () => {
            // Don't destroy the map instance on unmount to preserve state
            // Only clear markers to prevent memory leaks
            if (markersRef.current.length > 0) {
                markersRef.current.forEach(marker => marker.setMap(null));
                markersRef.current = [];
                cameraMarkersRef.current.clear();
            }
        };
    }, [mapLoaded, mapInitialized]);

    // Debounced marker update function
    const updateMarkers = (isZoomChange = false) => {
        if (!mapInstanceRef.current || !cameras || cameras.length === 0) {
            return;
        }

        console.log('🔄 Updating markers...', isZoomChange ? '(zoom change)' : '(data change)');
        
        // Get cameras with valid coordinates
        const camerasToUse = camerasWithCoords.filter(camera => {
            const coords = getCameraCoordinates(camera);
            return coords !== null;
        });

        // Check if all cameras that can be geocoded have been processed
        const totalCamerasNeedingCoords = cameras.filter(camera => {
            // If camera already has coordinates, it doesn't need geocoding
            const coords = getCameraCoordinates(camera);
            if (coords !== null) {
                return false;
            }
            // Check if camera has location data that can be geocoded
            const locationParts = [
                camera.location?.exactLocation,
                camera.location?.city,
                camera.location?.state,
                camera.location?.country
            ].filter(Boolean);
            return locationParts.length > 0;
        }).length;

        const camerasWithExistingCoords = cameras.filter(camera => {
            const coords = getCameraCoordinates(camera);
            return coords !== null;
        }).length;

        const totalExpectedCameras = totalCamerasNeedingCoords + camerasWithExistingCoords;
        const allMarkersReady = camerasToUse.length >= totalExpectedCameras || !geocodingInProgress;

        console.log(`📊 Marker loading status:`, {
            totalCameras: cameras.length,
            camerasWithCoords: camerasToUse.length,
            totalExpectedCameras,
            geocodingInProgress,
            allMarkersReady
        });

        // Clear existing markers once and rebuild all
        console.log('Clearing all existing markers');
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        cameraMarkersRef.current.clear();

        if (camerasToUse.length === 0) {
            console.log('No cameras with coordinates available');
            setMarkersFullyLoaded(allMarkersReady);
            return;
        }

        // Group cameras by location for clustering based on zoom level
        const locationGroups = new Map();
        
        // Determine clustering precision based on zoom level
        let precisionDigits;
        if (currentZoom >= 15) {
            precisionDigits = 6; // Very high precision - show individual cameras
        } else if (currentZoom >= 12) {
            precisionDigits = 5; // High precision - minimal clustering
        } else if (currentZoom >= 10) {
            precisionDigits = 4; // Medium precision - moderate clustering
        } else if (currentZoom >= 8) {
            precisionDigits = 3; // Low precision - more clustering
        } else {
            precisionDigits = 2; // Very low precision - heavy clustering
        }
        
        console.log(`Zoom level: ${currentZoom}, using precision: ${precisionDigits} digits`);
        
        camerasToUse.forEach(camera => {
            const coords = getCameraCoordinates(camera);
            if (!coords) {
                console.error(`No valid coordinates for camera ${camera.name}`);
                return;
            }
            
            const { lat, lng } = coords;
            
            // Round coordinates to group cameras together based on zoom level
            const locationKey = `${lat.toFixed(precisionDigits)},${lng.toFixed(precisionDigits)}`;
            
            if (!locationGroups.has(locationKey)) {
                locationGroups.set(locationKey, []);
            }
            locationGroups.get(locationKey).push(camera);
        });

        console.log(`Creating markers for ${locationGroups.size} locations with ${camerasToUse.length} total cameras`);

        // Create markers for each location group
        Array.from(locationGroups.entries()).forEach(([locationKey, camerasAtLocation]) => {
            const [baseLat, baseLng] = locationKey.split(',').map(parseFloat);
            
            // Determine clustering behavior based on zoom level and number of cameras
            const shouldCluster = currentZoom < 14 && camerasAtLocation.length > 3;
            const shouldSpread = currentZoom >= 12 && camerasAtLocation.length <= 10;
            
            if (camerasAtLocation.length === 1) {
                // Single camera - create regular marker
                const camera = camerasAtLocation[0];
                const marker = createSingleCameraMarker(camera, baseLat, baseLng);
                if (marker) {
                    markersRef.current.push(marker);
                    cameraMarkersRef.current.set(camera._id, marker);
                }
            } else if (shouldCluster && !shouldSpread) {
                // Create a cluster marker for multiple cameras
                const clusterMarker = createClusterMarker(camerasAtLocation, baseLat, baseLng);
                if (clusterMarker) {
                    markersRef.current.push(clusterMarker);
                    // Add all camera IDs to the marker tracking
                    camerasAtLocation.forEach(camera => {
                        cameraMarkersRef.current.set(camera._id, clusterMarker);
                    });
                }
            } else {
                // Spread individual markers in a pattern
                camerasAtLocation.forEach((camera, index) => {
                    let offsetLat = baseLat;
                    let offsetLng = baseLng;
                    
                    if (camerasAtLocation.length > 1) {
                        // Calculate offset distance based on zoom level
                        let offsetDistance = 0.002; // Base offset
                        if (currentZoom >= 15) {
                            offsetDistance = 0.0005; // Very small offset for high zoom
                        } else if (currentZoom >= 12) {
                            offsetDistance = 0.001; // Small offset for medium zoom
                        }
                        
                        // Arrange in a circle or grid pattern
                        if (camerasAtLocation.length <= 8) {
                            // Circle pattern for small groups
                            const angle = (index * 2 * Math.PI) / camerasAtLocation.length;
                            offsetLat = baseLat + (offsetDistance * Math.cos(angle));
                            offsetLng = baseLng + (offsetDistance * Math.sin(angle));
                        } else {
                            // Grid pattern for larger groups
                            const gridSize = Math.ceil(Math.sqrt(camerasAtLocation.length));
                            const row = Math.floor(index / gridSize);
                            const col = index % gridSize;
                            offsetLat = baseLat + ((row - gridSize/2) * offsetDistance);
                            offsetLng = baseLng + ((col - gridSize/2) * offsetDistance);
                        }
                    }
                    
                    const marker = createSingleCameraMarker(camera, offsetLat, offsetLng);
                    if (marker) {
                        markersRef.current.push(marker);
                        cameraMarkersRef.current.set(camera._id, marker);
                    }
                });
            }
        });

        // Update map bounds only for initial camera loading, not for zoom changes
        if (camerasToUse.length > 0 && !isZoomChange) {
            const bounds = new window.google.maps.LatLngBounds();
            camerasToUse.forEach(camera => {
                const coords = getCameraCoordinates(camera);
                if (coords) {
                    bounds.extend({ lat: coords.lat, lng: coords.lng });
                }
            });

            if (camerasToUse.length === 1) {
                const coords = getCameraCoordinates(camerasToUse[0]);
                if (coords) {
                    mapInstanceRef.current.setCenter({ lat: coords.lat, lng: coords.lng });
                    mapInstanceRef.current.setZoom(15);
                }
            } else {
                mapInstanceRef.current.fitBounds(bounds);
            }
        }

        // Mark markers as fully loaded when all expected markers are created
        // Don't reset loading state for zoom changes
        if (!isZoomChange) {
            setMarkersFullyLoaded(allMarkersReady);
        }
        
        console.log(`✅ Created ${markersRef.current.length} markers, fully loaded: ${allMarkersReady}`);
    };

    // Update markers when cameras with coordinates change, but debounce the updates
    useEffect(() => {
        if (!mapInitialized) return;

        // Mark as not fully loaded when starting to process new data
        setMarkersFullyLoaded(false);

        // Clear existing timeout
        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
        }

        // Debounce updates to prevent excessive re-rendering
        updateTimeoutRef.current = setTimeout(() => {
            updateMarkers();
        }, 500); // 500ms debounce

        return () => {
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }
        };
    }, [mapInitialized, camerasWithCoords]); // Removed currentZoom from dependencies

    // Handle zoom changes separately without showing loading overlay
    useEffect(() => {
        if (!mapInitialized || !markersFullyLoaded) return;

        // Only update clustering when zoom changes significantly (2+ levels)
        const zoomDiff = Math.abs(currentZoom - lastSignificantZoom);
        if (zoomDiff >= 2) {
            console.log(`Significant zoom change: ${lastSignificantZoom} -> ${currentZoom} (diff: ${zoomDiff})`);
            
            const updateTimeout = setTimeout(() => {
                updateMarkers(true); // Pass true to indicate this is a zoom change
                setLastSignificantZoom(currentZoom);
            }, 300); // Slightly longer timeout for stability

            return () => clearTimeout(updateTimeout);
        }
    }, [currentZoom, lastSignificantZoom]);


    // Function to geocode location string to coordinates
    const geocodeLocation = async (locationString) => {
        if (!window.google || !window.google.maps) return null;

        // Check cache first
        if (geocodingCache.has(locationString)) {
            console.log(`✓ Using cached geocoding result for "${locationString}"`);
            return geocodingCache.get(locationString);
        }

        return new Promise((resolve, reject) => {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: locationString }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const location = results[0].geometry.location;
                    const coords = {
                        latitude: location.lat(),
                        longitude: location.lng()
                    };
                    
                    // Cache the result
                    geocodingCache.set(locationString, coords);
                    
                    console.log(`✓ Geocoded "${locationString}" to:`, coords);
                    resolve(coords);
                } else if (status === 'REQUEST_DENIED') {
                    console.error('Geocoding Service: This API project is not authorized to use this API. For more information on authentication and Google Maps JavaScript API services please see: https://developers.google.com/maps/documentation/javascript/get-api-key');
                    setApiBlocked(true);
                    reject(new Error(`Geocoding failed: ${status}`));
                } else {
                    console.warn(`Geocoding failed for "${locationString}": ${status}`);
                    reject(new Error(`Geocoding failed: ${status}`));
                }
            });
        });
    };

    // Process cameras and geocode their locations
    useEffect(() => {
        const processCameras = async () => {
            if (!mapLoaded || cameras.length === 0) return;

            // Check if all cameras already have coordinates (no geocoding needed)
            const allCamerasHaveCoords = cameras.every(camera => {
                const coords = getCameraCoordinates(camera);
                return coords !== null;
            });
            
            if (allCamerasHaveCoords) {
                console.log('All cameras already have coordinates, no geocoding needed');
                setCamerasWithCoords(cameras);
                return;
            }

            // Check if we already have processed cameras for this camera set
            const cameraIds = cameras.map(c => c._id).sort().join(',');
            const cacheKey = `cameras_${cameraIds}`;
            
            if (geocodingCache.has(cacheKey)) {
                console.log('Using cached processed cameras');
                setCamerasWithCoords(geocodingCache.get(cacheKey));
                return;
            }

            setGeocodingInProgress(true);
            const processed = [];

            for (const camera of cameras) {
                // If camera already has coordinates, use them
                const coords = getCameraCoordinates(camera);
                if (coords) {
                    processed.push(camera);
                    setCamerasWithCoords([...processed]); // incremental update
                    continue;
                }

                // Create location string from camera location data
                const locationParts = [
                    camera.location?.exactLocation,
                    camera.location?.city,
                    camera.location?.state,
                    camera.location?.country
                ].filter(Boolean);

                if (locationParts.length === 0) {
                    console.warn(`Camera "${camera.name}" has no location data`);
                    continue;
                }

                const locationString = locationParts.join(', ');
                console.log(`Geocoding camera "${camera.name}" location: "${locationString}"`);

                try {
                    const coords = await geocodeLocation(locationString);
                    if (coords) {
                        processed.push({ ...camera, coordinates: coords });
                        setCamerasWithCoords([...processed]); // incremental update
                    }
                } catch (error) {
                    console.error(`Error geocoding camera "${camera.name}":`, error);
                }

                // Small delay to avoid rate limiting
                await new Promise(resolve => setTimeout(resolve, 150));
            }

            // Cache the processed cameras
            geocodingCache.set(cacheKey, processed);
            setGeocodingInProgress(false);
        };

        processCameras();
    }, [cameras, mapLoaded]);

    // Render loading state only when API is not loaded
    if (!mapLoaded) {
        return (
            <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-gray-600">Loading Google Maps...</p>
                </div>
            </div>
        );
    }

    // Render API key required state
    if (!apiKey) {
        return (
            <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Google Maps API Key Required</h3>
                    <p className="text-gray-600 mb-4">
                        To display camera locations on the map, you need to configure a Google Maps API key.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                        <h4 className="font-medium text-blue-900 mb-2">Setup Instructions:</h4>
                        <ol className="text-sm text-blue-800 space-y-1">
                            <li>1. Get a Google Maps API key from <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                            <li>2. Enable "Maps JavaScript API"</li>
                            <li>3. Create a `.env` file in the frontend directory</li>
                            <li>4. Add: <code className="bg-blue-100 px-1 rounded">VITE_GOOGLE_MAPS_API_KEY=your_api_key_here</code></li>
                            <li>5. Restart the development server</li>
                        </ol>
                    </div>
                </div>
            </div>
        );
    }

    // Render API blocked state
    if (apiBlocked) {
        return (
            <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Google Maps API Blocked</h3>
                    <p className="text-gray-600 mb-4">
                        Your Google Maps API key is blocked. This usually happens due to domain restrictions or API enablement issues.
                    </p>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
                        <h4 className="font-medium text-red-900 mb-2">Fix Instructions:</h4>
                        <ol className="text-sm text-red-800 space-y-1">
                            <li>1. Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                            <li>2. Select your project and go to "APIs & Services" &gt; "Credentials"</li>
                            <li>3. Click on your API key</li>
                            <li>4. Under "Application restrictions", set to "None" or add your domain</li>
                            <li>5. Under "API restrictions", make sure "Maps JavaScript API" is enabled</li>
                            <li>6. Save changes and wait a few minutes</li>
                        </ol>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            <div
                ref={mapRef}
                className="w-full h-[600px] rounded-lg shadow-lg"
                style={{ minHeight: '600px' }}
            />

            {/* Loading overlay until all markers are fully loaded */}
            {!markersFullyLoaded && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                    <div className="bg-white rounded-lg p-6 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600 mb-2">Loading camera locations on map...</p>
                        <div className="text-sm text-gray-500">
                            {camerasWithCoords.length} of {cameras.length} cameras loaded
                        </div>
                    </div>
                </div>
            )}


            {/* Camera Preview on Click - Centered, Clean UI */}
            {selectedCamera && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
                    onClick={() => setSelectedCamera(null)}
                >
                    <div
                        className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Camera Header */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                <div className={`w-3 h-3 rounded-full ${selectedCamera.isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                                <h3 className="text-lg font-semibold text-gray-900">{selectedCamera.name}</h3>
                            </div>
                            <button
                                onClick={() => setSelectedCamera(null)}
                                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Live Camera Feed */}
                        <div className="mb-6 bg-gray-900 rounded-lg overflow-hidden aspect-video relative">
                            {selectedCamera.isOnline ? (
                                <SimpleCameraStream 
                                    camera={selectedCamera}
                                    hideControls={false}
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-white">
                                    <div className="text-center">
                                        <Camera className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                        <p className="text-sm">Camera offline</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Full Screen Button */}
                        <button
                            onClick={() => {
                                if (onCameraClick) {
                                    onCameraClick(selectedCamera);
                                }
                                setSelectedCamera(null);
                            }}
                            className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-md"
                        >
                            <Maximize2 className="h-5 w-5" />
                            <span>Open Full Screen</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoogleMapsView; 